import { config, isDevMode } from "@/shared/lib/config";
import {
  fetchLookupForInfinite,
  hydrateLookupItem,
  hydrateLookupItems,
} from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { FormInfiniteMultiCombobox } from "@/shared/ui/FormInfiniteMultiCombobox";
import { FormCheckboxCard } from "@/shared/ui/FormCheckboxCard";
import {
  Button,
  FormNumber,
  FormRoot,
  FormSelect,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { SparklesIcon } from "lucide-react";
import type { z } from "zod";
import { CreateClassTemplateSchema } from "../../domain/contracts/templates";

type ClassTypeItem = { id: string; name: string };
type LevelItem = { id: string; name: string };
type InstructorItem = { id: string; name: string };

const ClassTemplateFormSchema = CreateClassTemplateSchema;
export type ClassTemplateFormValues = z.infer<typeof ClassTemplateFormSchema>;

type ClassTemplateFormProps = {
  initialValues?: Partial<ClassTemplateFormValues>;
  onSubmit: (values: ClassTemplateFormValues) => void;
  submitting?: boolean;
};

export function ClassTemplateForm({
  initialValues,
  onSubmit,
  submitting,
}: ClassTemplateFormProps) {
  const methods = RHF.useForm<ClassTemplateFormValues>({
    resolver: zodResolver(ClassTemplateFormSchema),
    defaultValues: {
      classTypeId: initialValues?.classTypeId || "",
      levelId: initialValues?.levelId || "",
      duration: initialValues?.duration ?? undefined,
      description: initialValues?.description || "",
      status: initialValues?.status || "active",
      onlineRegistration: initialValues?.onlineRegistration ?? false,
      allowedInstructorIds: initialValues?.allowedInstructorIds || [],
    },
  });

  const handleAutoFill = () => {
    methods.reset({
      classTypeId: "",
      levelId: "",
      duration: 60,
      description: "",
      status: "active",
      onlineRegistration: false,
      allowedInstructorIds: [],
    });
  };

  return (
    <FormRoot methods={methods} onSubmit={onSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormInfiniteCombobox<ClassTypeItem>
          name="classTypeId"
          label="ປະເພດຄລາດສ໌"
          placeholder="ເລືອກປະເພດຄລາດສ໌"
          requiredMark
          queryKey={["master", "class-types", "lookup"]}
          queryFn={({ search, pageParam }) =>
            fetchLookupForInfinite<ClassTypeItem>(
              `${config.apiUrl}/master/class-types/lookup`,
              { search, pageParam },
            )
          }
          preloadQueryFn={(id) =>
            hydrateLookupItem<ClassTypeItem>(
              `${config.apiUrl}/master/class-types/lookup`,
              id,
            ).then((item) => item ?? null)
          }
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
        />

        <FormInfiniteCombobox<LevelItem>
          name="levelId"
          label="ລະດັບ"
          placeholder="ເລືອກລະດັບ"
          requiredMark
          queryKey={["master", "levels", "lookup"]}
          queryFn={({ search, pageParam }) =>
            fetchLookupForInfinite<LevelItem>(
              `${config.apiUrl}/master/levels/lookup`,
              { search, pageParam },
            )
          }
          preloadQueryFn={(id) =>
            hydrateLookupItem<LevelItem>(
              `${config.apiUrl}/master/levels/lookup`,
              id,
            ).then((item) => item ?? null)
          }
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
        />

        <FormNumber
          name="duration"
          label="ລາຍການ (ນາທີ)"
          placeholder="ປ້ອນລາຍການເປັນນາທີ"
          requiredMark
        />

        <FormSelect
          label="ສະຖານະ"
          name="status"
          options={[
            { value: "active", label: "ໃຊ້ງານ" },
            { value: "inactive", label: "ບໍ່ໃຊ້ງານ" },
          ]}
        />
      </div>

      <FormTextarea
        name="description"
        label="ຄຳອະທິບາຍ"
        placeholder="ປ້ອນຄຳອະທິບາຍ (ບໍ່ບັງຄັບ)"
        rows={3}
      />

      <FormCheckboxCard
        name="onlineRegistration"
        label="ເປີດຮັບລົງທະບຽນອອນລາຍ"
        description="ເປີດແລ້ວຄລາດສ໌ນີ້ຈະຂຶ້ນໃຫ້ເລືອກໃນໜ້າລົງທະບຽນຮຽນ ປິດແລ້ວໃບສະໝັກໃໝ່ເລືອກຄລາດສ໌ນີ້ບໍ່ໄດ້"
      />

      <div className="rounded-xl border bg-card p-6">
        <h3 className="mb-4 flex items-center gap-2 font-semibold text-lg">
          <span className="size-2 rounded-full bg-red-600" />
          ຜູ້ສອນທີ່ອະນຸຍາດ (ບໍ່ບັງຄັບ)
        </h3>
        <p className="mb-4 text-muted-foreground text-sm">
          ຖ້າບໍ່ເລືອກ ຈະອະນຸຍາດໃຫ້ຜູ້ສອນທຸກຄົນສາມາດສອນໄດ້
        </p>
        <FormInfiniteMultiCombobox<InstructorItem>
          name="allowedInstructorIds"
          label="ຜູ້ສອນທີ່ອະນຸຍາດ"
          placeholder="ຄົ້ນຫາຜູ້ສອນ..."
          queryKey={["master", "instructors", "lookup"]}
          queryFn={({ search, pageParam }) =>
            fetchLookupForInfinite<InstructorItem>(
              `${config.apiUrl}/master/instructors/lookup`,
              { search, pageParam },
            )
          }
          preloadQueryFn={(ids) =>
            hydrateLookupItems<InstructorItem>(
              `${config.apiUrl}/master/instructors/lookup`,
              ids,
            )
          }
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
        />
      </div>

      <div className="flex justify-end gap-2 border-t pt-4">
        {isDevMode && (
          <Button
            type="button"
            variant="outline"
            onClick={handleAutoFill}
            disabled={submitting}
          >
            <SparklesIcon className="size-4" />
            Auto Fill
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={() => methods.reset()}
          disabled={submitting}
        >
          ລ້າງ
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
        </Button>
      </div>
    </FormRoot>
  );
}
